import type { NpcArchetype } from "./contracts.js";
import type { WeaponId } from "./definitions.js";

export const NPC_ATTACK_KINDS = [
  "none",
  "shot",
  "charge",
  "repair",
  "emp",
  "missile-salvo",
  "barrage",
] as const;
export type NpcAttackKind = (typeof NPC_ATTACK_KINDS)[number];
export const ORDINARY_NPC_ARCHETYPES = [
  "raider",
  "sniper",
  "minelayer",
  "charger",
  "support",
  "disruptor",
] as const;
const NPC_BEHAVIORS = [...ORDINARY_NPC_ARCHETYPES, "elite", "boss", "transport"] as const;
export type NpcBehavior = (typeof NPC_BEHAVIORS)[number];
export const ALIEN_NPC_ARCHETYPES = [
  "alien-scout",
  "alien-artillery",
  "alien-disruptor",
] as const;
export const NPC_ARCHETYPES = [...NPC_BEHAVIORS, ...ALIEN_NPC_ARCHETYPES] as const;
export const NPC_MECHANICS = {
  chargeSpeed: 500,
  chargeDuration: 0.55,
  supportRepairRange: 260,
  supportRepairRate: 6,
  disruptorEnergyDrain: 12,
  bossBarrageRadius: 180,
  bossFlakHalfAngle: 0.3,
  bossFlakRange: 1000,
};
export interface NpcDefinition {
  id: NpcArchetype;
  behavior: NpcBehavior;
  name: string;
  hp: number;
  shield: number;
  radius: number;
  speed: number;
  turnRate: number;
  preferredRange: number;
  attackRange: number;
  charge: number;
  cooldown: number;
  weaponId: WeaponId;
  damageMultiplier: number;
}
const BASE_NPCS: Record<NpcBehavior, NpcDefinition> = {
  raider: {
    id: "raider",
    behavior: "raider",
    name: "Налётчик",
    hp: 100,
    shield: 25,
    radius: 20,
    speed: 180,
    turnRate: 2.6,
    preferredRange: 220,
    attackRange: 410,
    charge: 0.6,
    cooldown: 1.6,
    weaponId: "pulse",
    damageMultiplier: 0.75,
  },
  sniper: {
    id: "sniper",
    behavior: "sniper",
    name: "Ракетный снайпер",
    hp: 100,
    shield: 25,
    radius: 20,
    speed: 130,
    turnRate: 2.6,
    preferredRange: 560,
    attackRange: 700,
    charge: 1.7,
    cooldown: 2.7,
    weaponId: "missile",
    damageMultiplier: 0.85,
  },
  minelayer: {
    id: "minelayer",
    behavior: "minelayer",
    name: "Минный заградитель",
    hp: 140,
    shield: 40,
    radius: 20,
    speed: 130,
    turnRate: 2.6,
    preferredRange: 360,
    attackRange: 560,
    charge: 0.8,
    cooldown: 3.2,
    weaponId: "pulse",
    damageMultiplier: 0.75,
  },
  charger: {
    id: "charger",
    behavior: "charger",
    name: "Таранщик",
    hp: 110,
    shield: 20,
    radius: 24,
    speed: 210,
    turnRate: 3.5,
    preferredRange: 250,
    attackRange: 480,
    charge: 1.2,
    cooldown: 2.4,
    weaponId: "flak",
    damageMultiplier: 5 / 7,
  },
  support: {
    id: "support",
    behavior: "support",
    name: "Ремонтник",
    hp: 100,
    shield: 35,
    radius: 22,
    speed: 140,
    turnRate: 2.6,
    preferredRange: 350,
    attackRange: 500,
    charge: 0.9,
    cooldown: 2,
    weaponId: "laser",
    damageMultiplier: 0.35,
  },
  disruptor: {
    id: "disruptor",
    behavior: "disruptor",
    name: "Подавитель",
    hp: 105,
    shield: 55,
    radius: 22,
    speed: 150,
    turnRate: 2.6,
    preferredRange: 430,
    attackRange: 540,
    charge: 1,
    cooldown: 2.8,
    weaponId: "ion",
    damageMultiplier: 0.65,
  },
  elite: {
    id: "elite",
    behavior: "elite",
    name: "Командир патруля",
    hp: 320,
    shield: 120,
    radius: 32,
    speed: 100,
    turnRate: 2.6,
    preferredRange: 350,
    attackRange: 410,
    charge: 2,
    cooldown: 3.8,
    weaponId: "pulse",
    damageMultiplier: 1.25,
  },
  boss: {
    id: "boss",
    behavior: "boss",
    name: "Разрушитель",
    hp: 600,
    shield: 150,
    radius: 52,
    speed: 75,
    turnRate: 1.3,
    preferredRange: 480,
    attackRange: 900,
    charge: 2,
    cooldown: 2.8,
    weaponId: "flak",
    damageMultiplier: 1,
  },
  transport: {
    id: "transport",
    behavior: "transport",
    name: "Транспорт «Вега»",
    hp: 240,
    shield: 90,
    radius: 36,
    speed: 0,
    turnRate: 2.6,
    preferredRange: 0,
    attackRange: 0,
    charge: 0,
    cooldown: 0,
    weaponId: "pulse",
    damageMultiplier: 0,
  },
};

export const NPCS: Record<NpcArchetype, NpcDefinition> = {
  ...BASE_NPCS,
  "alien-scout": {
    ...BASE_NPCS.raider,
    id: "alien-scout",
    name: "Чужой разведчик",
  },
  "alien-artillery": {
    ...BASE_NPCS.sniper,
    id: "alien-artillery",
    name: "Чужой стрелок",
  },
  "alien-disruptor": {
    ...BASE_NPCS.disruptor,
    id: "alien-disruptor",
    name: "Чужой подавитель",
  },
};
